import React from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

interface TablePaginationProps {
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  rowsPerPage: number;
  totalRecords: number;
  pageStart: number;
  pageEnd: number;
}

export const TablePagination: React.FC<TablePaginationProps> = ({
  page,
  setPage,
  rowsPerPage,
  totalRecords,
  pageStart,
  pageEnd,
}) => {
  const { t } = useTranslation();
  const totalPages = Math.ceil(totalRecords / rowsPerPage);
  const currentPage = page + 1;

  const renderPageNumbers = (): JSX.Element | null => {
    if (totalPages <= 1) return null;

    const pages: JSX.Element[] = [];

    // For small number of pages, show all pages
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(
          <Button
            key={i}
            variant={currentPage === i ? "contained" : "outlined"}
            size="small"
            onClick={() => setPage(i - 1)}
          >
            {i}
          </Button>,
        );
      }
    } else {
      // For larger number of pages, show smart pagination
      // Always show first page
      pages.push(
        <Button
          key={1}
          variant={currentPage === 1 ? "contained" : "outlined"}
          size="small"
          onClick={() => setPage(0)}
        >
          1
        </Button>,
      );

      // Show ellipsis after first page if needed
      if (currentPage > 3) {
        pages.push(
          <Typography key="dots1" variant="body2" sx={{ px: 1 }}>
            ...
          </Typography>,
        );
      }

      // Calculate range of middle pages to show
      let start: number, end: number;

      if (currentPage <= 3) {
        start = 2;
        end = 3;
      } else if (currentPage >= totalPages - 2) {
        start = totalPages - 2;
        end = totalPages - 1;
      } else {
        start = currentPage - 1;
        end = currentPage + 1;
      }

      for (let i = start; i <= end; i++) {
        pages.push(
          <Button
            key={i}
            variant={currentPage === i ? "contained" : "outlined"}
            size="small"
            onClick={() => setPage(i - 1)}
          >
            {i}
          </Button>,
        );
      }

      // Show ellipsis before last page if needed
      if (currentPage < totalPages - 2) {
        pages.push(
          <Typography key="dots2" variant="body2" sx={{ px: 1 }}>
            ...
          </Typography>,
        );
      }

      // Always show last page
      pages.push(
        <Button
          key={totalPages}
          variant={currentPage === totalPages ? "contained" : "outlined"}
          size="small"
          onClick={() => setPage(totalPages - 1)}
        >
          {totalPages}
        </Button>,
      );
    }

    return <>{pages}</>;
  };

  return (
    <Box
      sx={{
        p: 2,
        backgroundColor: "#f8f9fa",
        borderTop: "1px solid #dee2e6",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      {/* Records info */}
      <Typography variant="body2" color="textSecondary">
        {t("paygap.employeeData.pagination.showing", {
          start: pageStart,
          end: pageEnd,
          total: totalRecords,
        })}
      </Typography>

      {/* Page numbers - centered */}
      <Box display="flex" justifyContent="center" flex={1}>
        {renderPageNumbers()}
      </Box>

      {/* Previous/Next buttons - right side */}
      <Stack direction="row" spacing={1}>
        <Button
          size="small"
          onClick={() => setPage(Math.max(0, page - 1))}
          disabled={page === 0}
        >
          {t("paygap.employeeData.button.previous")}
        </Button>
        <Button
          size="small"
          onClick={() => setPage(page + 1)}
          disabled={(page + 1) * rowsPerPage >= totalRecords}
        >
          {t("paygap.employeeData.button.next")}
        </Button>
      </Stack>
    </Box>
  );
};
